import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Public as PublicIcon,
  LockOutlined as LockOutlinedIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import {
  tienePermisosModulo,
  puedeAccionFacturacion,
} from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader, fpInputSx } from 'components/financepro';
import { useProductosMaterialContext } from 'context';

const PROGRAMA_AREA_GEOGRAFICA_VENTAS = 'areageograficaventas';

/**
 * Área Geográfica Ventas: CRUD de zonas.
 * Las zonas listadas aquí pueden usarse para filtrar reportes (ej. PrintListado).
 */
const AreaGeograficaVentas = () => {
  const { controller, selectedCompany, showSuccess, showError } = useProductosMaterialContext();
  const { nombreNegocio, usuarioLog, claveLog, roleDetalles } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const puedeLeer = tienePermisosModulo(roleDetalles, PROGRAMA_AREA_GEOGRAFICA_VENTAS);
  const puedeModificar = puedeAccionFacturacion(
    roleDetalles,
    PROGRAMA_AREA_GEOGRAFICA_VENTAS,
    'modificar'
  );
  const puedeBorrar = puedeAccionFacturacion(
    roleDetalles,
    PROGRAMA_AREA_GEOGRAFICA_VENTAS,
    'borrar'
  );

  const [zonas, setZonas] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [dialogoAbierto, setDialogoAbierto] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [editando, setEditando] = useState(null);
  const [zona, setZona] = useState('');
  const [guardando, setGuardando] = useState(false);

  const cargarZonas = useCallback(async () => {
    if (!puedeLeer || !selectedCompany?.db_empresa) return;
    setCargando(true);
    try {
      const response = await fetch(servidor + '/api/clientesbackend_listarareas', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
        }),
      });
      if (response.ok) {
        const data = await response.json();
        setZonas(data.zonas || []);
      } else {
        const errorData = await response.json().catch(() => ({}));
        showError(errorData.Error || 'Error al cargar zonas');
      }
    } catch {
      showError('Error de conexión al cargar zonas');
    } finally {
      setCargando(false);
    }
  }, [puedeLeer, selectedCompany, usuarioLog, claveLog, servidor, showError]);

  useEffect(() => {
    cargarZonas();
  }, [cargarZonas]);

  const handleAbrirDialogo = (item = null) => {
    if (!puedeModificar) {
      showError('No tiene permiso para modificar las zonas.');
      return;
    }
    if (item) {
      setModoEdicion(true);
      setEditando(item);
      setZona(item.zona || '');
    } else {
      setModoEdicion(false);
      setEditando(null);
      setZona('');
    }
    setDialogoAbierto(true);
  };

  const handleCerrarDialogo = () => {
    setDialogoAbierto(false);
    setEditando(null);
    setZona('');
  };

  const handleGuardar = async () => {
    if (!puedeModificar) {
      showError('No tiene permiso para guardar zonas.');
      return;
    }
    if (!usuarioLog) {
      showError('Sesión inválida: falta usuario de login');
      return;
    }
    const nombreZona = (zona || '').trim();
    if (!nombreZona) {
      showError('Ingrese el nombre de la zona');
      return;
    }
    setGuardando(true);
    try {
      const body = {
        id_empresa: selectedCompany,
        usuario: usuarioLog || '',
        password: claveLog || '',
        zona: nombreZona,
      };
      if (modoEdicion && editando) {
        body.area_id = editando.id;
      }
      const response = await fetch(servidor + '/api/clientesbackend_guardararea', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (response.ok) {
        showSuccess(modoEdicion ? 'Zona actualizada' : 'Zona guardada');
        handleCerrarDialogo();
        cargarZonas();
      } else {
        const errorData = await response.json().catch(() => ({}));
        showError(errorData.Error || 'Error al guardar');
      }
    } catch {
      showError('Error de conexión al guardar');
    } finally {
      setGuardando(false);
    }
  };

  const handleEliminar = async (item) => {
    if (!puedeBorrar) {
      showError('No tiene permiso para eliminar zonas.');
      return;
    }
    if (!usuarioLog) {
      showError('Sesión inválida: falta usuario de login');
      return;
    }
    if (!window.confirm(`¿Eliminar la zona "${item.zona}"?`)) return;
    try {
      const response = await fetch(servidor + '/api/clientesbackend_eliminararea', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          area_id: item.id,
        }),
      });
      if (response.ok) {
        showSuccess('Zona eliminada');
        cargarZonas();
      } else {
        const errorData = await response.json().catch(() => ({}));
        showError(errorData.Error || 'Error al eliminar');
      }
    } catch {
      showError('Error de conexión al eliminar');
    }
  };

  const cardShellSx = {
    borderRadius: 2.5,
    border: `1px solid ${FP.color.border}`,
    bgcolor: FP.color.cardBg,
    boxShadow: FP.shadow.soft,
    overflow: 'hidden',
  };

  const filterHeaderSx = {
    px: { xs: 2, md: 3 },
    py: 1.6,
    borderBottom: `1px solid ${FP.color.border}`,
    bgcolor: '#FAFBFE',
    display: 'flex',
    alignItems: 'center',
    gap: 1.2,
  };

  const cellHeadSx = {
    fontWeight: 700,
    fontSize: '0.75rem',
    color: FP.color.textSecondary,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    display: 'block',
    mb: 0.5,
  };

  if (!puedeLeer) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
        <PageHeader
          title="Área geográfica de ventas"
          subtitle={
            nombreNegocio
              ? `${nombreNegocio} · Definición de zonas`
              : 'Definición de zonas'
          }
        />
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', justifyContent: 'center' }}>
          <Card
            elevation={0}
            sx={{ ...cardShellSx, maxWidth: 560, width: '100%', textAlign: 'center', p: { xs: 3, md: 4 } }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 1.6,
                mx: 'auto',
                mb: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: FP.color.dangerSoft,
                color: FP.color.danger,
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 30 }} />
            </Box>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: '1.25rem',
                color: FP.color.textPrimary,
                mb: 1,
                letterSpacing: '-0.01em',
              }}
            >
              Acceso denegado
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.95rem', mb: 1.2 }}>
              No tiene permiso del programa «areageograficaventas» en roles para administrar las zonas geográficas.
            </Typography>
            <Typography sx={{ color: FP.color.textMuted, fontSize: '0.85rem' }}>
              Por favor, contacte al administrador del sistema para solicitar los permisos necesarios.
            </Typography>
          </Card>
        </Box>
      </Box>
    );
  }

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
      <PageHeader
        title="Área geográfica de ventas"
        subtitle={
          nombreNegocio
            ? `${nombreNegocio} · Definición de zonas`
            : 'Definición de zonas'
        }
      />

      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Card elevation={0} sx={{ ...cardShellSx, mb: 3 }}>
          <Box sx={filterHeaderSx}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <PublicIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Zonas de ventas
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleAbrirDialogo()}
              disabled={!puedeModificar || !usuarioLog}
              sx={{
                height: 38,
                bgcolor: FP.color.navy,
                color: '#FFFFFF',
                borderRadius: 1.4,
                textTransform: 'none',
                fontWeight: 700,
                boxShadow: 'none',
                '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
                '&.Mui-disabled': { bgcolor: FP.color.border, color: FP.color.textMuted },
              }}
            >
              Nueva zona
            </Button>
          </Box>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.92rem', lineHeight: 1.5, mb: 2 }}>
              Defina las zonas para ventas. Podrá filtrar por zona en reportes (p. ej. listados impresos).
            </Typography>

            {cargando ? (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                <Typography sx={{ color: FP.color.textSecondary, fontWeight: 600 }}>Cargando…</Typography>
              </Box>
            ) : zonas.length === 0 ? (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                <DescriptionIcon sx={{ fontSize: 56, color: FP.color.textMuted, mb: 2 }} />
                <Typography sx={{ color: FP.color.textPrimary, fontWeight: 700 }} gutterBottom>
                  No hay zonas definidas
                </Typography>
                <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.9rem' }} textAlign="center" px={2}>
                  Use «Nueva zona» para crear la primera.
                </Typography>
              </Box>
            ) : (
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{ border: `1px solid ${FP.color.border}`, borderRadius: 2, overflow: 'auto' }}
              >
                <Table sx={{ tableLayout: 'fixed', width: '100%', minWidth: { xs: 520, sm: 680 } }}>
                  <colgroup>
                    {['12%', '68%', '20%'].map((w, i) => (
                      <col key={i} style={{ width: w }} />
                    ))}
                  </colgroup>
                  <TableBody>
                    <TableRow
                      sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 2,
                        '& td': {
                          border: 'none',
                          borderBottom: `2px solid ${FP.color.border}`,
                          bgcolor: FP.color.surfaceAlt,
                        },
                      }}
                    >
                      <TableCell>
                        <Typography component="span" sx={cellHeadSx}>
                          ID
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={cellHeadSx}>
                          Zona
                        </Typography>
                      </TableCell>
                      <TableCell className="no-print">
                        <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'center', width: '100%' }}>
                          Acciones
                        </Typography>
                      </TableCell>
                    </TableRow>
                    {zonas.map((item) => (
                      <TableRow key={item.id} hover>
                        <TableCell>{item.id}</TableCell>
                        <TableCell
                          sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                          title={item.zona || '—'}
                        >
                          {item.zona || '—'}
                        </TableCell>
                        <TableCell align="center" className="no-print">
                          <IconButton
                            size="small"
                            onClick={() => handleAbrirDialogo(item)}
                            disabled={!puedeModificar || !usuarioLog}
                            sx={{
                              color: FP.color.accentBlue,
                              '&:hover': { bgcolor: FP.color.accentBlueSoft },
                              '&.Mui-disabled': { color: FP.color.textMuted },
                            }}
                            title="Modificar"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          {puedeBorrar && (
                            <IconButton
                              size="small"
                              onClick={() => handleEliminar(item)}
                              disabled={!usuarioLog}
                              sx={{
                                color: FP.color.danger,
                                '&:hover': { bgcolor: FP.color.dangerSoft },
                                '&.Mui-disabled': { color: FP.color.textMuted },
                              }}
                              title="Eliminar"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Box>

      <Dialog
        open={dialogoAbierto}
        onClose={handleCerrarDialogo}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            boxShadow: FP.shadow.md,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            px: { xs: 2, md: 3 },
            py: 1.6,
            borderBottom: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
            display: 'flex',
            alignItems: 'center',
            gap: 1.2,
          }}
        >
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: 1.2,
              bgcolor: FP.color.accentBlueSoft,
              color: FP.color.accentBlue,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <PublicIcon sx={{ fontSize: 18 }} />
          </Box>
          <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
            {modoEdicion ? 'Editar zona' : 'Nueva zona'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, md: 3 } }}>
          <TextField
            fullWidth
            label="Zona"
            value={zona}
            onChange={(e) => setZona(e.target.value)}
            required
            placeholder="Ej. Norte, Sur, Capital"
            helperText="Nombre de la zona geográfica para ventas"
            disabled={!puedeModificar || !usuarioLog}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 1.5, mt: 2, ...fpInputSx }}
          />
          {modoEdicion && editando && (
            <Typography sx={{ color: FP.color.textMuted, fontSize: '0.78rem' }}>
              ID: {editando.id}
            </Typography>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: { xs: 2, md: 3 },
            py: 1.5,
            borderTop: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
            gap: 1,
          }}
        >
          <Button
            onClick={handleCerrarDialogo}
            startIcon={<CloseIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              color: FP.color.textSecondary,
              borderRadius: 1.4,
              '&:hover': { bgcolor: FP.color.surfaceAlt },
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleGuardar}
            disabled={guardando || !puedeModificar || !usuarioLog}
            sx={{
              textTransform: 'none',
              borderRadius: 1.4,
              fontWeight: 700,
              bgcolor: FP.color.navy,
              color: '#FFFFFF',
              boxShadow: 'none',
              '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
              '&.Mui-disabled': { bgcolor: FP.color.border, color: FP.color.textMuted },
            }}
          >
            {modoEdicion ? 'Modificar' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AreaGeograficaVentas;
